import { z } from "zod";
import type { Cart } from "@/graphql/storefront/generated";
import { getReferralAttribute } from "@/cart/referralAttribute";

export const REFERRAL_PREFIX = "FRIEND-";

const REFERRAL_CODE_SHAPE = /^[A-Z0-9][A-Z0-9-]{1,39}$/;

export const looksLikeReferralCode = (code: string): boolean =>
  REFERRAL_CODE_SHAPE.test(code.trim().toUpperCase());

const ReferralRewardSchema = z.enum(["referral-bag-pass", "bag-pass", "none"]);

/**
 * Which bag pass a referral discount gives away free with each Matic.
 * ReferralCandy codes give the referral edition, creator codes the standard
 * pass, and any other discount neither.
 */
export type ReferralReward = z.infer<typeof ReferralRewardSchema>;

export type ResolvedReferral = { code: string; reward: ReferralReward };

const ReferralValidationSchema = z.discriminatedUnion("valid", [
  z.object({
    valid: z.literal(true),
    code: z.string(),
    reward: ReferralRewardSchema,
  }),
  z.object({ valid: z.literal(false) }),
]);

/** Response contract for `GET /api/referral/validate`. */
export type ReferralValidationResponse = z.infer<
  typeof ReferralValidationSchema
>;

// Bumped whenever the validate response changes shape, so a response cached
// under the old shape (the endpoint is cached for an hour) is never reused.
const REFERRAL_VALIDATION_VERSION = "2";

const referralLookups = new Map<string, Promise<ResolvedReferral | null>>();

/**
 * Resolves a candidate code through the validate endpoint to its canonical code
 * and reward, or null when it is not a referral. Rejects when the endpoint is
 * unavailable, so callers can tell an outage from an unknown code. Lookups are
 * shared for the page's lifetime, so the toast, the configurator and the cart
 * cannot disagree about the same code.
 */
export const resolveReferral = (
  code: string,
): Promise<ResolvedReferral | null> => {
  const key = code.trim().toUpperCase();
  const existingLookup = referralLookups.get(key);

  if (existingLookup) {
    return existingLookup;
  }

  const lookup = fetch(
    `/api/referral/validate?code=${encodeURIComponent(key)}&v=${REFERRAL_VALIDATION_VERSION}`,
  ).then(async (response) => {
    if (!response.ok) {
      throw new Error(`Referral validation returned ${response.status}`);
    }
    // A body that does not parse rejects like an outage: an unknown reward
    // must leave the cart alone rather than be read as "no pass".
    const result = ReferralValidationSchema.parse(await response.json());
    return result.valid ? { code: result.code, reward: result.reward } : null;
  });

  referralLookups.set(key, lookup);
  lookup.catch(() => referralLookups.delete(key));

  return lookup;
};

const isReferral = (code: string, markedCode?: string): boolean =>
  code.startsWith(REFERRAL_PREFIX) || code === markedCode;

export const normalizeReferralCode = (
  code?: string | null,
): string | undefined => {
  const trimmedCode = code?.trim();
  return trimmedCode ? trimmedCode.toUpperCase() : undefined;
};

export const getReferralCodes = (cart?: Cart): string[] => {
  const markedCode = getReferralAttribute(cart);
  return (
    cart?.discountCodes
      ?.map((discountCode) => discountCode.code.toUpperCase())
      .filter(
        (code): code is string => Boolean(code) && isReferral(code, markedCode),
      ) || []
  );
};

export const getApplicableReferralCodes = (cart?: Cart): string[] => {
  const markedCode = getReferralAttribute(cart);
  return (
    cart?.discountCodes
      ?.filter(
        (discountCode) =>
          isReferral(discountCode.code.toUpperCase(), markedCode) &&
          discountCode.applicable,
      )
      .map((discountCode) => discountCode.code.toUpperCase()) || []
  );
};

export const getFirstReferralCode = (cart?: Cart): string | undefined => {
  return getReferralCodes(cart)[0];
};
